import { useDiscountProductPaymentsQuery } from '@/entities/catalog-discount'
import { useGetProductPaymentsQuery } from '@/entities/Payments/api/productApiSlice'

import { useProductSsrData } from '../context/product-ssr-data-context'

import { useIsDiscountProductPage } from './useProduct'

/**
 * Product payments query that resolves from the SSR context when it's available — so every payments
 * consumer (credit panel, flags, credit tab, …) renders identically on the server and the client's
 * first render (no hydration mismatch) and skips the client request. Falls back to the normal query
 * when there's no SSR data (e.g. client-side navigation without a provider).
 * Discount product pages use `discount/products/{id}/payments` instead of `/products/{id}/payments`.
 */
export const useProductPaymentsQuery = (productId: number) => {
	const isDiscountProductPage = useIsDiscountProductPage()
	const ssr = useProductSsrData()
	const hasSsr = ssr?.payments != null && !isDiscountProductPage
	const discountProductId = isDiscountProductPage && productId ? String(productId) : ''

	const discountQuery = useDiscountProductPaymentsQuery(discountProductId)
	const query = useGetProductPaymentsQuery(productId, {
		skip: hasSsr || isDiscountProductPage
	})

	if (isDiscountProductPage) {
		return {
			...query,
			data: discountQuery.data,
			isLoading: discountQuery.isLoading,
			isFetching: discountQuery.isFetching,
			isError: discountQuery.isError
		}
	}

	if (hasSsr) {
		return { ...query, data: ssr.payments, isLoading: false, isFetching: false, isError: false }
	}

	return query
}
